import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { track } from '../lib/trackDeferred';
import { isOwnBrand, msaSubtotal as calcMsaSubtotal } from '../lib/brand';

const CartContext = createContext(null);
const LS_KEY = 'msa_cart_v1';
const WL_KEY = 'msa_wishlist_v1';

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [open, setOpen] = useState(false);

  const openCart = useCallback((currentItems, currentSubtotal) => {
    setOpen(true);
    track.viewCart(currentItems, currentSubtotal);
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) setItems(JSON.parse(raw));
      const wraw = localStorage.getItem(WL_KEY);
      if (wraw) setWishlist(JSON.parse(wraw));
    } catch (_) {}
  }, []);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
  }, [items]);
  useEffect(() => {
    localStorage.setItem(WL_KEY, JSON.stringify(wishlist));
  }, [wishlist]);

  const addItem = useCallback((product, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.id === product.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], qty: copy[idx].qty + qty };
        return copy;
      }
      return [
        ...prev,
        // isOwn is captured here because the cart line is a snapshot: the free
        // delivery threshold applies to MSA goods only and must not depend on a
        // later lookup. Lines saved before this existed have no flag and count
        // as partner goods, which under-promises rather than over-promises.
        { id: product.id, name: product.name, price: product.price, image: product.image,
          qty, isOwn: isOwnBrand(product) },
      ];
    });
    setOpen(true);
    track.addToCart(product, qty);
  }, []);

  const updateQty = useCallback((id, qty) => {
    setItems((prev) =>
      prev
        .map((i) => (i.id === id ? { ...i, qty: Math.max(1, qty) } : i))
    );
  }, []);

  const removeItem = useCallback((id) => {
    setItems((prev) => {
      const item = prev.find((i) => i.id === id);
      if (item) track.removeFromCart(item);
      return prev.filter((i) => i.id !== id);
    });
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  // Один ключ на товар. Досі різні сторінки клали в список різне: каталог і
  // картка товару — slug, сторінка партнерів і 404 — UUID. Тому серце, вимкнене
  // в одному місці, лишалось увімкненим в іншому, а той самий товар міг
  // потрапити в список двічі.
  const wishKey = (productId, product) =>
    (product && (product.slug || product.id)) || productId;

  const toggleWishlist = useCallback((productId, product = null) => {
    const key = wishKey(productId, product);
    setWishlist((prev) => {
      const isAdding = !prev.includes(key);
      if (isAdding && product) track.addToWishlist(product);
      return isAdding ? [...prev, key] : prev.filter((id) => id !== key);
    });
  }, []);

  const isWished = useCallback(
    (product) => {
      if (!product) return false;
      return wishlist.includes(product.slug) || wishlist.includes(product.id);
    },
    [wishlist]
  );

  // Звести збережені ключі до канонічних і викинути товари, яких уже немає
  // в каталозі. Інакше лічильник у шапці рахує позиції, яких сторінка
  // показати не може.
  const syncWishlist = useCallback((products) => {
    const canonical = products.map((p) => p.slug || p.id).filter(Boolean);
    setWishlist((prev) =>
      prev.length === canonical.length && prev.every((k, i) => k === canonical[i])
        ? prev
        : canonical
    );
  }, []);

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + i.price * i.qty, 0),
    [items]
  );
  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  // Sum of MSA-brand lines only — what the free delivery threshold is measured
  // against. Delivery itself is still paid by the recipient at Nova Poshta
  // (PayerType=Recipient in nova_poshta.py), so this drives messaging only.
  const msaTotal = useMemo(() => calcMsaSubtotal(items), [items]);

  const value = {
    items,
    wishlist,
    open,
    setOpen,
    openCart,
    addItem,
    updateQty,
    removeItem,
    clearCart,
    toggleWishlist,
    isWished,
    syncWishlist,
    subtotal,
    msaTotal,
    count,
  };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
